import type { ColumnFilterConfig, RangeFilterOption } from '~components/TableList.types';
import type { TableFilteringState } from '~store';

/**
 * Checks if a value matches a range filter option
 * Range is inclusive of min and exclusive of max: [min, max)
 *
 * @param value - The numeric value to check
 * @param option - The range filter option with min and/or max
 * @returns True if the value falls within the range, false otherwise
 */
const matchesRangeOption = (value: number, option: RangeFilterOption): boolean => {
  const { min, max } = option;
  const aboveMin = min === undefined || value >= min;
  const belowMax = max === undefined || value < max;
  return aboveMin && belowMax;
};

/**
 * Applies all active filters to the data array.
 * Returns rows that match ALL filter columns (AND logic between columns).
 *
 * @param data - The array of data to filter
 * @param filters - The current filtering state from the store
 * @param filterConfigs - The filter configurations for each column
 * @returns Filtered data array
 */
const applyFilters = <T>(data: T[], filters: TableFilteringState, filterConfigs: ColumnFilterConfig<T>[]): T[] => {
  // No filters active - return all data
  if (filters.length === 0) return data;

  const configMap = new Map(filterConfigs.map((c) => [c.columnId, c]));

  // Check each active filter (AND logic between different columns)
  return data.filter((row) =>
    filters.every((filter) => {
      const config = configMap.get(filter.columnId);
      if (!config) return true;

      const rowValue = config.getValue(row);
      if (rowValue == null || rowValue === '') return false;

      // Discrete filter: exact string match
      if (config.type !== 'range') return String(rowValue) === filter.value;

      // Range filter: find matching option and check bounds
      const option = config.options?.find((o) => o.label === filter.value);
      if (!option) return false;

      const numValue = Number(rowValue);
      return !Number.isNaN(numValue) && matchesRangeOption(numValue, option);
    }),
  );
};

export { applyFilters, matchesRangeOption };
